import type { DocumentLayoutTemplateRow, DocumentLayoutV1 } from './documentLayoutTypes';
import { applyGrayscaleToDataUrl } from './layoutImageEffects';

type LayoutApiErrorBody = {
  error?: string;
  issues?: Array<{ message?: string; code?: string; key?: string }>;
};

/** Prefer concrete validation issues over a vague top-level error string. */
export function formatDocumentLayoutApiError(
  json: LayoutApiErrorBody | null,
  fallback: string,
): string {
  const issues = (json?.issues ?? [])
    .map((i) => (i.message ?? '').trim())
    .filter(Boolean);
  const uniqueIssues = [...new Set(issues)];
  if (uniqueIssues.length > 0) {
    const head = (json?.error ?? '').trim();
    // Avoid "Layout ungültig." alone — issues carry the actionable detail.
    if (head && !/^layout ungültig\.?$/i.test(head) && !head.endsWith(':')) {
      return `${head}: ${uniqueIssues.join(' · ')}`;
    }
    return uniqueIssues.join(' · ');
  }
  return (json?.error ?? '').trim() || fallback;
}

export async function fetchDocumentLayoutTemplates(): Promise<DocumentLayoutTemplateRow[]> {
  const res = await fetch('/api/settings/document-layout-templates', { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Vorlagen konnten nicht geladen werden.');
  }
  const json = (await res.json()) as { templates?: DocumentLayoutTemplateRow[] };
  return Array.isArray(json.templates) ? json.templates : [];
}

/**
 * Fail-open: returns null on any error or when no default exists.
 * Never throw — invoice PDF generation must continue without a template.
 */
export async function fetchDefaultDocumentLayoutTemplate(): Promise<DocumentLayoutTemplateRow | null> {
  try {
    const res = await fetch('/api/settings/document-layout-templates/default', {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { template?: DocumentLayoutTemplateRow | null };
    return json.template ?? null;
  } catch {
    return null;
  }
}

export async function fetchDocumentLayoutTemplate(
  id: string,
): Promise<DocumentLayoutTemplateRow | null> {
  const res = await fetch(`/api/settings/document-layout-templates/${encodeURIComponent(id)}`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Vorlage konnte nicht geladen werden.');
  }
  const json = (await res.json()) as { template?: DocumentLayoutTemplateRow };
  return json.template ?? null;
}

export async function createDocumentLayoutTemplate(params: {
  name: string;
  layout?: DocumentLayoutV1;
  is_default?: boolean;
}): Promise<DocumentLayoutTemplateRow> {
  const res = await fetch('/api/settings/document-layout-templates', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as LayoutApiErrorBody | null;
    throw new Error(formatDocumentLayoutApiError(json, 'Vorlage konnte nicht erstellt werden.'));
  }
  const json = (await res.json()) as { template: DocumentLayoutTemplateRow };
  return json.template;
}

export async function updateDocumentLayoutTemplate(
  id: string,
  patch: { name?: string; layout?: DocumentLayoutV1 },
): Promise<DocumentLayoutTemplateRow> {
  const res = await fetch(`/api/settings/document-layout-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as LayoutApiErrorBody | null;
    throw new Error(formatDocumentLayoutApiError(json, 'Vorlage konnte nicht gespeichert werden.'));
  }
  const json = (await res.json()) as { template: DocumentLayoutTemplateRow };
  return json.template;
}

export async function setDefaultDocumentLayoutTemplate(
  id: string,
): Promise<DocumentLayoutTemplateRow> {
  const res = await fetch(
    `/api/settings/document-layout-templates/${encodeURIComponent(id)}/default`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as LayoutApiErrorBody | null;
    throw new Error(formatDocumentLayoutApiError(json, 'Standard setzen fehlgeschlagen.'));
  }
  const json = (await res.json()) as { template: DocumentLayoutTemplateRow };
  return json.template;
}

/** Unset live/default — documents use the native layout again. */
export async function clearDefaultDocumentLayoutTemplate(
  id: string,
): Promise<DocumentLayoutTemplateRow> {
  const res = await fetch(
    `/api/settings/document-layout-templates/${encodeURIComponent(id)}/default`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as LayoutApiErrorBody | null;
    throw new Error(formatDocumentLayoutApiError(json, 'Standard aufheben fehlgeschlagen.'));
  }
  const json = (await res.json()) as { template: DocumentLayoutTemplateRow };
  return json.template;
}

export async function deleteDocumentLayoutTemplate(id: string): Promise<void> {
  const res = await fetch(`/api/settings/document-layout-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(json?.error || 'Löschen fehlgeschlagen.');
  }
}

export type DocumentLayoutAssetKind = 'banner' | 'signature';

export function documentLayoutAssetSrc(objectPath: string, bust?: number): string {
  const q = new URLSearchParams({ path: objectPath });
  if (bust != null) q.set('t', String(bust));
  return `/api/settings/document-layout-assets?${q.toString()}`;
}

export async function uploadDocumentLayoutAsset(
  templateId: string,
  kind: DocumentLayoutAssetKind,
  file: File,
): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(
    `/api/settings/document-layout-templates/${encodeURIComponent(templateId)}/assets/${kind}`,
    { method: 'POST', credentials: 'include', body: form },
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(json?.error || 'Bild konnte nicht hochgeladen werden.');
  }
  const json = (await res.json()) as { object_path?: string };
  const path = String(json.object_path ?? '').trim();
  if (!path) throw new Error('Upload ohne Speicherpfad.');
  return path;
}

/** Data-URL for react-pdf (cookies work via fetch; Image src needs embedded data). */
export async function fetchDocumentLayoutAssetDataUrl(
  objectPath: string,
): Promise<string | null> {
  try {
    const res = await fetch(documentLayoutAssetSrc(objectPath), {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.onerror = () =>
        reject(reader.error ?? new Error('Bild konnte nicht gelesen werden.'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Resolve all banner/signature paths on a layout to data URLs keyed by element id. */
export async function resolveLayoutAssetDataUrls(
  layout: DocumentLayoutV1 | null | undefined,
): Promise<Record<string, string>> {
  if (!layout) return {};
  const entries: Array<{ id: string; path: string; grayscale: boolean }> = [];
  for (const el of [...layout.header.elements, ...layout.footer.elements]) {
    if (
      (el.key === 'headerBanner' || el.key === 'signatureImage') &&
      (el.imageObjectPath ?? '').trim()
    ) {
      entries.push({
        id: el.id,
        path: el.imageObjectPath!.trim(),
        grayscale: el.key === 'headerBanner' && el.imageGrayscale === true,
      });
    }
  }
  const out: Record<string, string> = {};
  await Promise.all(
    entries.map(async ({ id, path, grayscale }) => {
      const dataUrl = await fetchDocumentLayoutAssetDataUrl(path);
      if (!dataUrl) return;
      if (grayscale) {
        const gray = await applyGrayscaleToDataUrl(dataUrl);
        out[id] = gray ?? dataUrl;
        return;
      }
      out[id] = dataUrl;
    }),
  );
  return out;
}
